import { useAppDispatch, useAppSelector } from "../app/hooks";
import {
  clearBooking,
  clearBookingPayment,
  selectBooking,
} from "../features/booking/bookingSlice";
import { selectHotel } from "../features/hotel/hotelSlice";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { getCurrentHost } from "../features/hotel/hotelApi";
import ChCheck from "../components/Svg/ChCheck";
import SummaryCard from "../components/Summary/SummaryCard";
import Button from "../components/Button";
import { Trans, useTranslation } from "react-i18next";
import CityHostSlider from "../components/Success/CityHostSlider";
import type { ICurrentCityHost } from "../features/hotel/hotelInterface";

function Success() {
  const dispatch = useAppDispatch();
  const hotel = useAppSelector(selectHotel);
  const booking = useAppSelector(selectBooking);
  const [curBooking, setCurBooking] = useState(booking);
  const [host, setHost] = useState<ICurrentCityHost | null>(null);
  const { t } = useTranslation();

  useEffect(() => {
    setCurBooking(booking);

    // Clear rooms, reservations and payment on success
    dispatch(clearBooking());
    dispatch(clearBookingPayment());

    // If hotel code is not available, do not attempt to fetch host information
    if(!hotel.HotelCode) return;
    
    // Get current host
    const currentHost = async () => {
      return await getCurrentHost(hotel.HotelCode);
    };
    currentHost().then((response) => {
      if (response.data.HasCityHost) {
        setHost(response.data.CurrentCityHost);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: "-12px", transition: { duration: 0 } }}
        animate={{ opacity: 1, y: "0px", transition: { duration: 0.4 } }}
        exit={{ opacity: 0, y: "-12px", transition: { duration: 0 } }}
        className="absolute inset-0 overflow-y-auto w-full"
      >
        <div className="relative bg-green-light py-12 px-8 overflow-hidden">
          <ChCheck className="absolute -bottom-20 right-0 h-96 w-80 text-custom-400 z-0" />
          <div className="relative flex flex-col gap-4 justify-center items-center z-10">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="36"
              height="36"
              viewBox="0 0 24 24"
              fill="currentColor"
              strokeWidth="2"
              className="text-custom-500"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M12 1C5.925 1 1 5.925 1 12s4.925 11 11 11 11-4.925 11-11S18.075 1 12 1zm4.768 9.14a1 1 0 1 0-1.536-1.28l-4.3 5.159-2.225-2.226a1 1 0 0 0-1.414 1.414l3 3a1 1 0 0 0 1.475-.067l5-6z"
              />
            </svg>

            <div className="text-custom-500 text-h4">
              {t("scenes.success.bookingConfirmed")}
            </div>
            <div className="text-h5 text-center">
              <Trans
                i18nKey="scenes.success.bookingConfirmedDescription"
                values={{ hotelName: hotel.Name[hotel.Culture.LanguageCode] }}
                components={{ ul: <ul />, li: <li />, br: <br /> }}
              />
            </div>
          </div>
        </div>

        <div className="mt-6 p-4 flex flex-col gap-6">
          <div className="flex flex-col gap-4">
            <div className="text-h4 leading-none">
              {t("scenes.success.bookingDetails")}
            </div>
            <SummaryCard booking={curBooking} />
          </div>

          <div className="flex flex-col gap-4">
            <div className="text-h4 leading-none">
              {t("scenes.success.whatsNext")}
            </div>

            <div className="text-gray-700">
              {t("scenes.success.connectWithHost")}
            </div>
          </div>

          {host && (
            <div className="relative bg-custom-100 rounded-xl text-black flex flex-col w-full gap-2 pb-6">
              <div className="relative w-full flex items-center gap-4 px-8 pt-6">
                <img
                  src={host.ProfilePhotoUrl}
                  alt=""
                  className="size-16 rounded-full object-cover object-center"
                />
                <div className="text-fluid-h3">{host.FirstName}</div>
                <div className="absolute top-4 right-4 rounded-md px-2 py-0.5 bg-green-light text-custom-500 flex items-center gap-1 text-fluid-xs">
                  <svg
                    width="4"
                    height="4"
                    viewBox="0 0 4 4"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle cx="2" cy="2" r="2" fill="#617A37" />
                  </svg>
                  <span>{t("scenes.success.online")}</span>
                </div>
              </div>

              <div className="flex flex-col px-8 w-full gap-2">
                {host.ProfileDescription && (
                  <div>{host.ProfileDescription}</div>
                )}
              </div>
              
              <CityHostSlider images={host.Media} peek={0.1} />

              <div className="w-full px-8">
                <Button
                  text={t("scenes.success.startChatting")}
                  onClick={() => {}}
                />
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </>
  );
}

export default Success;
